import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarDays, Clapperboard, Rocket, Trash2, Zap, Clock, TrendingUp } from "lucide-react";
import { sr } from "../../lib/sr";

const money = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function ReleaseCalendar() {
  const [data, setData] = useState(null);
  const [defaultLead, setDefaultLead] = useState(14);
  const [lead, setLead] = useState({}); // release_id -> lead days
  const [autoLaunch, setAutoLaunch] = useState({}); // release_id -> bool
  const [boost, setBoost] = useState({}); // release_id -> {on, peak, taper}
  const [busy, setBusy] = useState("");

  const load = () => {
    sr.releaseCalendar().then((r) => { setData(r.events || []); setDefaultLead(r.default_lead_days || 14); }).catch(() => setData([]));
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;

  // Group events by release.
  const groups = {};
  data.forEach((e) => { (groups[e.release_id] = groups[e.release_id] || { release: e, markets: [] }).markets.push(e); });
  const releases = Object.values(groups);

  const schedule = async (rid) => {
    setBusy(rid);
    try {
      const b = boost[rid] || {};
      const r = await sr.scheduleRelease(rid, { lead_days: lead[rid] ?? defaultLead, auto_launch: !!autoLaunch[rid], boost: !!b.on, boost_peak: b.peak || 2, taper_days: b.taper ?? 3 });
      toast.success(`Scheduled ${r.count} market campaign(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not schedule"); } finally { setBusy(""); }
  };
  const launch = async (sid) => {
    setBusy(sid);
    try { const r = await sr.launchReleaseSchedule(sid); toast.success(r.message || "Submitted for go-live"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); } finally { setBusy(""); }
  };
  const remove = async (sid) => {
    setBusy(sid);
    try { await sr.deleteReleaseSchedule(sid); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); } finally { setBusy(""); }
  };

  const STATUS = { scheduled_draft: ["Draft ready", "sr-badge-gray"], submitted_for_go_live: ["Submitted for go-live", "sr-badge-amber"], launched: ["Launched", "sr-badge-green"] };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="release-calendar-page">
      <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
        <CalendarDays size={22} /> Release Calendar
      </h1>
      <p className="sr-muted" style={{ marginTop: 6 }}>
        Each movie's markets and opening dates, timed into a per-market campaign. Scheduling builds a <strong>draft</strong> campaign that starts a set lead time before opening; launch stays with your admins. Turn on auto-submit to route a draft into go-live automatically on its start date.
      </p>

      {releases.length === 0 ? (
        <div className="sr-card sr-empty" style={{ marginTop: 14, textAlign: "center", padding: 34 }} data-testid="release-empty">
          <Clapperboard size={26} style={{ color: "var(--muted)" }} />
          <p style={{ marginTop: 8 }}>No movie releases yet. Add a movie in your Catalog and give it release markets + an opening date to see it here.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
          {releases.map(({ release, markets }) => (
            <div key={release.release_id} className="sr-card" data-testid={`release-${release.release_id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <strong style={{ color: "var(--navy)", fontSize: 16 }}>{release.movie_name}</strong>
                  <div className="sr-muted" style={{ fontSize: 12 }}>
                    Opening {fmt(release.release_date)}{release.territory ? ` · ${release.territory}` : ""}{release.release_type ? ` · ${release.release_type}` : ""} · {markets.length} market{markets.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ fontSize: 12, color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center" }}>
                    <Clock size={13} /> Lead
                    <input type="number" min={0} max={180} value={lead[release.release_id] ?? defaultLead}
                      onChange={(e) => setLead((s) => ({ ...s, [release.release_id]: Number(e.target.value) }))}
                      className="sr-input sr-input-sm" style={{ width: 64 }} data-testid={`release-lead-${release.release_id}`} /> days
                  </label>
                  <label style={{ fontSize: 12, color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!autoLaunch[release.release_id]} onChange={(e) => setAutoLaunch((s) => ({ ...s, [release.release_id]: e.target.checked }))} data-testid={`release-auto-${release.release_id}`} />
                    <Zap size={13} /> Auto-submit on start
                  </label>
                  <label style={{ fontSize: 12, color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!(boost[release.release_id] || {}).on} onChange={(e) => setBoost((s) => ({ ...s, [release.release_id]: { ...(s[release.release_id] || { peak: 2, taper: 3 }), on: e.target.checked } }))} data-testid={`release-boost-${release.release_id}`} />
                    <TrendingUp size={13} /> Opening-week boost
                  </label>
                  {(boost[release.release_id] || {}).on && (
                    <label style={{ fontSize: 12, color: "var(--navy)", display: "inline-flex", gap: 4, alignItems: "center" }}>
                      peak
                      <input type="number" min={1} max={10} step={0.5} value={(boost[release.release_id] || {}).peak ?? 2}
                        onChange={(e) => setBoost((s) => ({ ...s, [release.release_id]: { ...(s[release.release_id] || {}), on: true, peak: Number(e.target.value) } }))}
                        className="sr-input sr-input-sm" style={{ width: 54 }} data-testid={`release-boost-peak-${release.release_id}`} />x
                    </label>
                  )}
                  <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => schedule(release.release_id)} disabled={busy === release.release_id} data-testid={`release-schedule-${release.release_id}`}>
                    {busy === release.release_id ? <Loader2 size={13} className="sr-spin" /> : <><CalendarDays size={13} /> Schedule all markets</>}
                  </button>
                </div>
              </div>
              <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                {markets.map((m) => {
                  const [label, cls] = STATUS[m.schedule_status] || [];
                  return (
                    <div key={m.market} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 10px", background: "#F8FAFC", borderRadius: 8, flexWrap: "wrap" }} data-testid={`release-market-${release.release_id}-${m.market}`}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <strong style={{ color: "var(--navy)", fontSize: 13 }}>{m.market}</strong>
                        <span className="sr-muted" style={{ fontSize: 12 }}>starts {fmt(m.start_date)} · {money(m.budget)}</span>
                        {label && <span className={`sr-badge ${cls}`}>{label}</span>}
                        {m.auto_launch && m.scheduled && <span className="sr-badge sr-badge-gray"><Zap size={10} /> auto</span>}
                        {m.boost && m.boost_plan && m.boost_plan.length > 0 && (
                          <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 1, height: 18 }} title="Opening-week budget ramp" data-testid={`release-boost-curve-${m.schedule_id}`}>
                            {m.boost_plan.map((e, i) => {
                              const mx = Math.max(...m.boost_plan.map((x) => x.multiplier || 1));
                              return <span key={i} style={{ width: 4, height: `${Math.max(3, ((e.multiplier || 1) / mx) * 18)}px`, background: "var(--blue)", borderRadius: 1, opacity: 0.85 }} />;
                            })}
                            <span className="sr-badge sr-badge-gray" style={{ marginLeft: 4 }}><TrendingUp size={10} /> {m.boost_peak}x peak</span>
                          </span>
                        )}
                      </div>
                      {m.scheduled && (
                        <div style={{ display: "flex", gap: 6 }}>
                          {m.schedule_status === "scheduled_draft" && (
                            <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => launch(m.schedule_id)} disabled={busy === m.schedule_id} data-testid={`release-launch-${m.schedule_id}`}>
                              <Rocket size={12} /> Submit to go-live
                            </button>
                          )}
                          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => remove(m.schedule_id)} disabled={busy === m.schedule_id} data-testid={`release-remove-${m.schedule_id}`}><Trash2 size={12} /></button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
